/**
 * Quest Panel Component
 * Displays quests and allows claiming rewards
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Award, X } from 'lucide-react';

const QuestPanel = ({
  isOpen,
  onClose,
  quests,
  onClaimReward
}) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/80 p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="quest-panel-overlay"
    >
      <Card 
        className="w-full max-w-lg bg-gray-900 border-green-500/30 max-h-[90vh] overflow-hidden flex flex-col" 
        onClick={e => e.stopPropagation()}
        data-testid="quest-panel"
      >
        <CardHeader className="pb-2 shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-green-400" />
              Quests
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-quest-panel">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-white/60 text-sm">Complete quests to earn VibeCoins!</p>
        </CardHeader>
        <CardContent className="space-y-3 overflow-y-auto flex-1">
          {quests.length === 0 ? (
            <p className="text-white/40 text-center py-8">Loading quests...</p>
          ) : (
            quests.map(quest => (
              <div 
                key={quest.quest_id} 
                className={`p-3 rounded-lg border ${
                  quest.claimed 
                    ? 'bg-green-900/20 border-green-500/30' 
                    : quest.completed 
                      ? 'bg-yellow-900/20 border-yellow-500/40' 
                      : 'bg-black/30 border-white/10'
                }`}
                data-testid={`quest-item-${quest.quest_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        quest.quest_type === 'daily' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                      }`}>
                        {quest.quest_type}
                      </span>
                      <h4 className="text-white font-medium">{quest.title}</h4>
                    </div>
                    <p className="text-white/60 text-sm mt-1">{quest.description}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-2 bg-black/50 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all"
                          style={{ width: `${Math.min(100, (quest.progress / quest.requirement_count) * 100)}%` }}
                          data-testid={`quest-progress-${quest.quest_id}`}
                        />
                      </div>
                      <span className="text-white/60 text-xs">
                        {quest.progress}/{quest.requirement_count}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3 text-right shrink-0">
                    <p className="text-yellow-400 font-bold">+{quest.reward_vibecoins}</p>
                    {quest.completed && !quest.claimed && (
                      <Button 
                        onClick={() => onClaimReward(quest.quest_id)}
                        size="sm"
                        className="mt-1 bg-green-600 hover:bg-green-500 h-7 text-xs"
                        data-testid={`claim-quest-${quest.quest_id}`}
                      >
                        Claim!
                      </Button>
                    )}
                    {quest.claimed && (
                      <span className="text-green-400 text-xs">✓ Claimed</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QuestPanel;
